import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  Calendar,
  Users,
  Award,
  ExternalLink,
  Heart,
  Activity,
  Zap
} from 'lucide-react';
import HeartLogo from './HeartLogo';

const ClinicalStudy = ({ onBack }) => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const handleDownload = () => {
    window.open('https://customer-assets.emergentagent.com/job_cardiac-tech/artifacts/dn7vej3z_BCS%20Annual%20Conference%202026-Copy.pdf', '_blank');
  };

  const studyImages = [
    {
      src: "/images/echo_study_1.jpg",
      alt: "Doppler Echocardiogram Analysis",
      caption: "Real-time Doppler echocardiography measuring E/A ratio and diastolic function"
    },
    {
      src: "/images/echo_study_2.jpg",
      alt: "Photobiomodulation Treatment Session",
      caption: "Thor Giant Laser Cluster applied to cardiac apex with simultaneous monitoring"
    },
    {
      src: "/images/echo_study_3.jpg",
      alt: "Targeted Light Therapy Application",
      caption: "Echocardiography-guided photobiomodulation targeting the heart muscle"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors duration-200"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-4">
                <HeartLogo className="w-12 h-12" animate={true} />
                <span className="text-xl font-semibold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Nyquist Therapeutics
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium">
                First in Man Study
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/20 via-gray-900 to-teal-900/20 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center space-y-8"
            {...fadeInUp}
          >
            {/* Featured Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="flex justify-center mb-4"
            >
              <HeartLogo className="w-28 h-28 md:w-36 md:h-36" animate={true} />
            </motion.div>
            
            <div className="flex justify-center">
              <div className="inline-flex items-center space-x-3 bg-teal-500/20 text-teal-400 rounded-full px-6 py-3 border border-teal-500/30">
                <Award className="w-5 h-5" />
                <span className="font-semibold">BCS Annual Conference 2026</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold leading-tight max-w-5xl mx-auto">
              <span className="bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 bg-clip-text text-transparent">
                First in Man
              </span>
              <br />
              <span className="text-white text-2xl md:text-4xl">
                Use of Targeted, Echocardiography Guided, Photobiomodulation to Improve Cardiac Diastolic Function
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Groundbreaking pilot study presented at the British Cardiovascular Society Annual Conference
            </p>

            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button
                onClick={handleDownload}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
                data-testid="download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                <span>Download Abstract PDF</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Clinical Images Gallery */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">Clinical Study Images</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Real images from our first in man pilot study demonstrating the photobiomodulation procedure and echocardiographic assessment
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {studyImages.map((image, index) => (
              <motion.div
                key={index}
                className="bg-gray-900/70 backdrop-blur-sm rounded-2xl overflow-hidden border border-teal-500/20 hover:border-teal-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-teal-400 mb-2">{image.alt}</h3>
                  <p className="text-gray-400 text-sm">{image.caption}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Details */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-800/70 backdrop-blur-sm rounded-2xl p-8 border border-teal-500/30">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-400">Study Information</h3>
                </div>
                
                <div className="space-y-4 text-gray-300">
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-teal-400" />
                    <span><strong>Authors:</strong> Dr. Scott Murray, Mr. James Carroll</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-teal-400" />
                    <span><strong>Conference:</strong> BCS Annual Conference 2026</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Award className="w-5 h-5 text-teal-400" />
                    <span><strong>Affiliations:</strong> Nyquist Therapeutics, Venturi Cardiology, Thor Photomedicine Ltd</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/70 backdrop-blur-sm rounded-2xl p-8 border border-teal-500/30">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-400">Key Results</h3>
                </div>
                
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span><strong>32% increase</strong> in E velocity (early diastolic filling)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span>Significant improvement in <strong>E/A ratio</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span>Results exceed current pharmacological treatments (5-10% improvement)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span>Potential to move subjects out of diastolic dysfunction criteria</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-teal-900/20 to-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-teal-500/30">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-400">Mechanism</h3>
                </div>
                
                <div className="space-y-6 text-gray-300">
                  <p className="leading-relaxed">
                    This groundbreaking pilot study demonstrates the <strong>first in man</strong> use of targeted, 
                    echocardiography-guided photobiomodulation (PBM) to improve cardiac diastolic function.
                  </p>
                  
                  <p className="leading-relaxed">
                    Using the <strong>Thor Giant Laser Cluster</strong> device applied to the cardiac apex, 
                    the study measured changes in E/A ratio - a key indicator of diastolic function - 
                    before and after treatment.
                  </p>
                  
                  <p className="leading-relaxed">
                    The mechanism involves enhanced <strong>mitochondrial function</strong> and increased 
                    <strong> ATP generation</strong>, supporting the energy-intensive process of diastolic relaxation.
                  </p>
                </div>
              </div>

              <div className="bg-gray-800/70 backdrop-blur-sm rounded-2xl p-8 border border-teal-500/30">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-400">Download Full Abstract</h3>
                </div>
                
                <p className="text-gray-300 mb-6">
                  Download the complete conference abstract including methodology, results, 
                  and detailed findings from the pilot study.
                </p>
                
                <button
                  onClick={handleDownload}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                  data-testid="download-pdf-btn-2"
                >
                  <Download className="w-5 h-5" />
                  <span>Download PDF Abstract</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-gradient-to-r from-teal-900/20 to-cyan-800/20 backdrop-blur-sm rounded-3xl p-8 border border-teal-500/30"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center space-y-6">
              <h3 className="text-2xl font-bold text-teal-400">Conclusion</h3>
              <p className="text-gray-300 leading-relaxed">
                This pilot study successfully demonstrated the feasibility of targeted, echocardiography-guided 
                photobiomodulation on the heart muscle. The significant improvements in E velocity and E/A ratios 
                suggest potential applications for both individuals with cardiac pathology and healthy athletes 
                seeking cardiovascular optimization.
              </p>
              <div className="pt-4">
                <p className="text-sm text-gray-400">
                  <strong>Keywords:</strong> Photobiomodulation, Diastolic function, E/A ratio, Mitochondrial Function, Cardiovascular Optimisation
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-gray-900 border-t border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 mb-4">For more information about this research</p>
          <a
            href="mailto:team@nyquisttherapeutics.com"
            className="inline-flex items-center space-x-2 text-teal-400 hover:text-red-300 transition-colors"
          >
            <span>team@nyquisttherapeutics.com</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ClinicalStudy;
